import * as PIXI from "pixi.js";
import { AppCore } from "../../api/core/AppCore";
import { Dictionary } from "../../util/collection/Dictionary";
import { EventObject } from "../../util/event/EventObject";
import { Logger } from "../../util/log/Logger";
import { Delay } from "../../util/time/Delay";
import { MouseUtil } from "../../util/view/MouseUtil";
import { SnapLayout } from "../control/layout/SnapLayout";
import { AssetManager } from "../manager/AssetManager";
import { LanguageManager } from "../manager/LanguageManager";
import { SnapshotManager } from "../manager/SnapshotManager";
import { SoundManager } from "../manager/SoundManager";
import { Renderer, ResizeEvent, } from "../renderer/Renderer";
import { CurrencyUtil } from "./../../util/currency/CurrencyUtil";
import { LocationManager } from "./../manager/LocationManager";
import { OrientationType } from "./../renderer/constant/RendererConstants";
import { AbstractViewProperties } from "./properties/AbstractViewProperties";
/**
 * AbstractView.ts
 *
 * The base class of every custom PIXI object.
 */
export class AbstractView extends PIXI.Container {
    /**
     * @returns `true` if `portraitProperties` exists and is distinct from `landscapeProperties`.
     */
    get hasPortraitProperties() {
        return (this.portraitProperties instanceof AbstractViewProperties &&
            !this.portraitProperties.equals(this.landscapeProperties));
    }
    getProperties() {
        return this.uiProperties;
    }
    /**
     * Calls `initialise`
     * @param properties - Landscape Properties
     * @param portraitProperties - Portrait Properties
     */
    constructor(properties, portraitProperties) {
        super();
        this.initialise(properties, portraitProperties);
    }
    debug_mouse(enable) {
        if (enable) {
            this.debug_mouse(false);
            let bounds = this.getBounds();
            if (bounds.width > 0 && bounds.height > 0) {
                this.savedEventMode = this.eventMode;
                this.eventMode = "static";
                this.hitArea = new PIXI.Rectangle(0, 0, this.width, this.height);
                MouseUtil.addMouseDown(this, this.debug_mouseDown, this);
                MouseUtil.addMouseMove(this, this.debug_mouseMove, this);
                MouseUtil.addMouseUp(this, this.debug_mouseUp, this);
            }
        }
        else {
            this._mouseDrag = undefined;
            this.eventMode = this.savedEventMode;
            MouseUtil.removeMouseDown(this, this.debug_mouseDown);
            MouseUtil.removeMouseMove(this, this.debug_mouseMove);
            MouseUtil.removeMouseUp(this, this.debug_mouseUp);
        }
    }
    debug_mouseDown(event) {
        this._originalPosition = this.position.clone();
        this._mouseDrag = event.global.clone();
    }
    debug_mouseMove(event) {
        const mousePosition = event.global.clone();
        if (this._mouseDrag && !this._mouseDrag.equals(mousePosition)) {
            let localMouseDrag = this.toLocal(this._mouseDrag);
            let localMousePos = this.toLocal(mousePosition);
            this.position.x =
                this._originalPosition.x + (localMouseDrag.x - localMousePos.x);
            this.position.y =
                this._originalPosition.y + (localMouseDrag.y - localMousePos.y);
        }
    }
    debug_mouseUp(event) {
        this._mouseDrag = null;
        Logger.debug(`<===============================>`); //`
        Logger.debug(`--------->${this.position.x},${this.position.y}`); //`
        Logger.debug(`<===============================>`); //`
    }
    /**
     * Saves a reference to most singletons and applies properties.
     * @param properties - Landscape properties.
     * @param portraitProperties - Portrait properties.
     */
    initialise(properties, portraitProperties) {
        this.createCore();
        this.createEventDispatcher();
        this.createAssetManager();
        this.createSoundManager();
        this.createLangManager();
        this.createSnapshotManager();
        this.createLocationManager();
        this.createRenderer();
        this.createProperties(properties, portraitProperties);
        this.createLayouts();
        this.createFilters();
        this.applyProperties();
        this.createAccessibility();
    }
    /**
     * Saves landscapeProperties and portraitProperties. Assigns uiProperties to the current orientation.
     *
     * There is no need to override this to reassign the properties.
     */
    createProperties(landscapeProperties, portraitProperties) {
        if (!landscapeProperties) {
            landscapeProperties = portraitProperties;
        }
        this.landscapeProperties = landscapeProperties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.getCurrentProperties();
        this.uiFilters = new Dictionary();
        if (this.uiProperties?.trackingId) {
            this.trackingId = this.uiProperties.trackingId;
            this.locationManager.add(this.trackingId, this);
        }
        this.responsive = this.hasPortraitProperties || !!this.trackingId;
    }
    getCurrentProperties(forceOrientation) {
        let properties = this.landscapeProperties;
        if (this.portraitProperties &&
            this.renderer.isPortrait(forceOrientation)) {
            properties = this.portraitProperties;
        }
        return properties;
    }
    createCore() {
        this.core = AppCore.getInstance();
    }
    createEventDispatcher() {
        // this.eventDispatcher = new EventDispatcher();
    }
    createAssetManager() {
        this.assetManager = AssetManager.getInstance();
    }
    createSoundManager() {
        this.soundManager = SoundManager.getInstance();
    }
    createLangManager() {
        this.langManager = LanguageManager.getInstance();
    }
    createSnapshotManager() {
        this.snapshotManager = SnapshotManager.getInstance();
    }
    createLocationManager() {
        this.locationManager = LocationManager.getInstance();
    }
    createRenderer() {
        this.renderer = Renderer.getInstance();
    }
    createLayouts() {
        this.layouts = [];
    }
    createFilters() {
        this.uiFilters.setValue(OrientationType.LANDSCAPE, this._createFilters(this.landscapeProperties));
        if (this.portraitProperties?.filterProperties &&
            !this.portraitProperties.filterProperties.every((value, index) => value.equals(this.landscapeProperties.filterProperties[index]))) {
            this.uiFilters.setValue(OrientationType.PORTRAIT, this._createFilters(this.portraitProperties));
        }
        else {
            this.uiFilters.setValue(OrientationType.PORTRAIT, this._createFilters(this.landscapeProperties));
        }
    }
    _createFilters(properties) {
        if (properties?.filterProperties) {
            return properties.filterProperties.map((prop) => prop.createFilter());
        }
        return null;
    }
    /**
     * Initialize all new fields here.
     */
    create() {
        this.createModel();
    }
    /**
     * Override with post-creation actions.
     */
    postCreate() { }
    createModel() { }
    destroy() {
        super.destroy();
        this.removeEventListeners();
        this.clearTrackingProperties();
        if (this.trackingId) {
            this.locationManager.remove(this.trackingId);
        }
        this.core = null;
        this.assetManager = null;
        this.soundManager = null;
        this.langManager = null;
        this.renderer = null;
        this.landscapeProperties = null;
        this.portraitProperties = null;
        this.uiProperties = null;
        this.running = null;
        this.layouts = null;
        this.landscapeTrackingProperties = null;
        this.portraitTrackingProperties = null;
        this.removeObjectRef(this);
    }
    removeObjectRef(obj) {
        const objRef = window.objRef;
        const keys = Object.keys(objRef);
        const delKey = keys.find((key) => objRef[key] === obj);
        if (delKey) {
            delete objRef[delKey];
        }
    }
    /**
     * Any additions to addEventListeners() must also be added to removeEventListeners().
     */
    addEventListeners() {
        if (this.responsive) {
            this.registerCore(ResizeEvent.RESIZE, this.handleResize);
            this.registerCore(ResizeEvent.ROTATE, this.handleRotate);
        }
        this.registerCore(ResizeEvent.TRACK_TARGETS, this.handleTrackTargets);
    }
    removeEventListeners() {
        this.unregisterCore(ResizeEvent.RESIZE);
        this.unregisterCore(ResizeEvent.ROTATE);
        this.unregisterCore(ResizeEvent.TRACK_TARGETS);
    }
    /**
     * Override with any custom positioning code.
     *
     * Called any time the canvas resizes.
     */
    handleResize(event) {
        if (!this.renderer.getRotated()) {
            const transform = this.getSafeTransform();
            for (const layout of this.layouts) {
                layout.snap(transform);
            }
        }
    }
    /**
     * Override with any custom code needed when switching orientations.
     *
     * `handleRotate` happens before `handleResize`.
     *
     * @see {@link OrientationType}
     */
    handleRotate(event) {
        const data = event?.getCurrentTarget() || {};
        this.uiProperties = this.getCurrentProperties(data.forceOrientation);
        if (this.responsive) {
            this.layouts = [];
            this.applyProperties();
        }
    }
    handleTrackTargets() {
        let currentTrackingProperties = this.landscapeTrackingProperties;
        if (this.renderer.isPortrait() && this.portraitTrackingProperties) {
            currentTrackingProperties = this.portraitTrackingProperties;
        }
        if (!currentTrackingProperties) {
            return;
        }
        let offset = new PIXI.Point();
        // DisplayObject.toLocal converts screen coordinates to the sprite's PARENT coordinate space
        // - i.e. you still have to adjust for scale to move the object to the correct place on the screen.
        // May need revisting if we try to track rotated objects
        this.toLocal(this.locationManager.getLocation(currentTrackingProperties.id)
            .position, null, offset, false);
        let trackingOffset = this.renderer.isPortrait() && this.portraitTrackingProperties
            ? this.portraitTrackingProperties.offset
            : this.landscapeTrackingProperties.offset;
        this.position.x += (offset.x + trackingOffset.x) * this.scale.x;
        this.position.y += (offset.y + trackingOffset.y) * this.scale.y;
    }
    /**
     * Used by `handleRotate` to get the new orientation's properties.
     */
    applyProperties() {
        this.resizeView(this, this.uiProperties);
        this.filters = this.uiFilters.getValue(this.renderer.getOrientation());
    }
    createAccessibility() {
        if (this.uiProperties?.accessibleOptions && this.uiProperties.accessibleOptions.accessible !== false) {
            this.accessible = true;
            this.accessibleChildren = this.uiProperties.accessibleOptions?.accessibleChildren ?? true;
            this.accessibleHint = this.uiProperties.accessibleOptions?.accessibleHint ?? "";
            this.accessibleTitle = this.uiProperties.accessibleOptions?.accessibleTitle ?? "";
            this.tabIndex = this.uiProperties.accessibleOptions?.tabIndex ?? 0;
        }
    }
    resizeView(view, properties) {
        if (properties?.position) {
            view.position.copyFrom(properties.position);
        }
        if (properties?.scale) {
            view.scale.copyFrom(properties.scale);
        }
        if (typeof properties?.rotation === "number") {
            view.rotation = properties.rotation;
        }
        if (properties?.hitArea) {
            view.hitArea = properties.hitArea;
        }
        if (typeof properties?.visible === "boolean") {
            view.visible = properties.visible;
        }
    }
    /**
     * @param delay - Time in milliseconds
     * @param handler - Function to be called
     * @param args - Agurments to be passed to `handler`
     * @returns new `Delay` instance
     */
    callAfter(delay, handler, args) {
        return Delay.create(delay, handler, this, args);
    }
    /**
     * Converts a number to a currency string.
     * @see {@link CurrencyUtil}
     */
    formatCurrency(value) {
        return CurrencyUtil.format(value);
    }
    addLayout(object, direction) {
        const layout = new SnapLayout(object, direction);
        this.layouts.push(layout);
        return layout;
    }
    addSnapshot(name, target, options) {
        this.snapshotManager.addTarget(name, target, options);
    }
    addPendingSnapshot(name, target, options, onStart, onFinish) {
        this.snapshotManager.addPendingTarget(name, target, options, onStart, onFinish);
    }
    /**
     * Register an event listener with the `core` event bus.
     * Only one function can be bound to an event per controller.
     *
     * Call `unregisterCore(type)` to remove the listener.
     */
    registerCore(event, handler) {
        this.core?.addEventListener(event.getType(), handler, this);
    }
    /**
     * Unregister an event listener from the `core` event bus.
     */
    unregisterCore(event) {
        this.core?.removeEventListener(event.getType(), this);
    }
    /**
     * Dispatch an event to the `core` event bus.
     *
     * @param event - The event to dispatch.
     * @param delay - Optional delay in milliseconds before dispatching the event.
     */
    dispatchCore(event, delay = 0) {
        if (delay > 0) {
            Delay.create(delay, this.core.dispatchEvent, this.core, event);
        }
        else {
            this.core.dispatchEvent(event);
        }
    }
    /**
     * Awaits all async event listeners for the given `event`.
     *
     * Most event listeners do not return promises so they resolve instantly.
     *
     * @param event - The event to dispatch.
     */
    async dispatchBlockingEvent(event) {
        await this.core.dispatchBlockingEvent(event);
    }
    run() {
        if (!this.running) {
            this.running = true;
            this.create();
            this.postCreate();
            this.addEventListeners();
            this.handleRotate();
            this.handleResize();
        }
    }
    sleep() {
        if (this.running) {
            this.running = false;
            this.removeEventListeners();
        }
    }
    /**
     * Wrapper for `position.copyFrom(otherPosition);`
     */
    setPosition(position) {
        if (position) {
            this.position.copyFrom(position);
        }
    }
    /**
     * Set tracking ID - i.e. the key used to obtain this object's tracking data
     */
    setTrackingId(id) {
        if (this.trackingId) {
            this.locationManager.remove(this.trackingId);
        }
        this.trackingId = id;
        this.locationManager.add(this.trackingId, this);
    }
    /**
     * Set tracking TARGET props - i.e., what object this object should follow
     */
    setTrackingProperties(properties, portraitProperties) {
        this.landscapeTrackingProperties = properties;
        if (portraitProperties && !properties.equals(portraitProperties)) {
            this.portraitTrackingProperties = portraitProperties;
        }
        this.handleTrackTargets();
    }
    clearTrackingProperties() {
        this.landscapeTrackingProperties = null;
        this.portraitTrackingProperties = null;
    }
    /** The width of the Container */
    getWidth() {
        return this.width;
    }
    /** The height of the Container */
    getHeight() {
        return this.height;
    }
    getSafeTransform() {
        let transform = new PIXI.Point(0, 0);
        if (!this.renderer.getRotated()) {
            let safeTransform = this.renderer.getSafeTransform();
            transform = safeTransform.clone();
        }
        return transform;
    }
    getTrackingData() {
        const id = this.trackingId;
        const position = this.getGlobalPosition();
        const transform = this.worldTransform;
        return { id, position, transform };
    }
    /**
     * @deprecated
     * @see {@link LocationManager}
     */
    updateTracking() { }
    isRunning() {
        return this.running;
    }
    /**
     * A wrapper to check if a function exists before calling it.
     *
     * @param handler - A function with a bound scope
     * @param args - Arguments for the function
     */
    callHandler(handler, args) {
        if (handler) {
            handler.call(args);
        }
    }
    /* Helper Methods */
    hide() {
        this.visible = false;
        this.unhighlightButtons();
    }
    unhighlightButtons() {
        this.children?.forEach((child) => {
            if (child.forceUnpressedState instanceof Function) {
                child.forceUnpressedState();
            }
        });
    }
    /**
     * Sets `this.visible` to true unless disabled by properties.
     */
    show() {
        if (typeof this.uiProperties?.visible !== "boolean" ||
            this.uiProperties?.visible) {
            this.visible = true;
        }
    }
    anchorRight(view, properties, offset) {
        if (view && properties) {
            let position = AbstractView.getAnchorPosition(properties);
            view.x = position.x + offset;
        }
    }
    anchorLeft(view, properties, offset) {
        if (view && properties) {
            let position = AbstractView.getAnchorPosition(properties);
            view.x = position.x - offset;
        }
    }
    anchorTop(view, properties, offset) {
        if (view && properties) {
            let position = AbstractView.getAnchorPosition(properties);
            view.y = position.y - offset;
        }
    }
    anchorBottom(view, properties, offset) {
        if (view && properties) {
            let position = AbstractView.getAnchorPosition(properties);
            view.y = position.y + offset;
        }
    }
    /**
     * @deprecated Use `selected ?? def`.
     */
    orDefault(selected, def) {
        if (selected !== undefined && selected !== null) {
            return selected;
        }
        return def;
    }
    static getAnchorPosition(properties) {
        let position;
        if (properties instanceof AbstractViewProperties) {
            position = properties.position;
        }
        else if (properties instanceof PIXI.Point) {
            position = properties;
        }
        return position;
    }
    centreObject(object = this, toParent) {
        let totalWidth = toParent
            ? object.parent.width
            : this.renderer.getViewport().width;
        object.x = (totalWidth - object.getLocalBounds().width) / 2;
        let totalHeight = toParent
            ? object.parent.height
            : this.renderer.getViewport().height;
        object.y = (totalHeight - object.getLocalBounds().height) / 2;
    }
    getViewBranch() {
        let views = [this];
        this.children.forEach((child) => {
            if (child instanceof AbstractView) {
                views = views.concat(child.getViewBranch());
            }
        });
        return views;
    }
    /**
     * Recursively walk up a view's parents calculating its global scale multiplied by `scale`.
     *
     * Could be converted to static because it takes `scale` as a parameter instead of reading `this.scale`.
     *
     * @param scale - Initial Point
     */
    getTransformedScale(parent, scale) {
        if (!parent) {
            return scale;
        }
        return this.getTransformedScale(parent.parent, new PIXI.Point(scale.x * parent.scale.x, scale.y * parent.scale.y));
    }
    forceRotate(orientation) {
        const views = this.getViewBranch().filter((child) => child.responsive);
        //trigger rotate and resize event in all responsive child objects
        views.forEach((view) => view.handleRotate(new EventObject(ResizeEvent.ROTATE.getType(), {
            forceOrientation: orientation,
        })));
        views.forEach((view) => view.handleResize());
    }
}
//# sourceMappingURL=AbstractView.js.map